import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface Integration {
  id: string;
  configuration: Record<string, any>;
  provider: string;
  state: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IntegrationsState {
  data: Integration[];
  loading: boolean;
  error: string | null;
  activating: string | null; // Track which platform is being activated
  deactivating: string | null; // Track which platform is being deactivated
  isFetched: boolean; // Track if data has been fetched
  updatingSettings: boolean; // Track TikTok (or other) settings update in progress
  updateSettingsError: string | null;
}

const initialState: IntegrationsState = {
  data: [],
  loading: false,
  error: null,
  activating: null,
  deactivating: null,
  isFetched: false,
  updatingSettings: false,
  updateSettingsError: null,
};

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    // Fetch integrations
    fetchIntegrationsRequest: (state, action: PayloadAction<{ restaurantId: string }>) => {
      state.loading = true;
      state.error = null;
    },
    fetchIntegrationsSuccess: (state, action: PayloadAction<Integration[]>) => {
      state.loading = false;
      state.data = action.payload;
      state.error = null;
      state.isFetched = true;
    },
    fetchIntegrationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isFetched = true;
    },

    
    // Activate integration (OAuth flow)
    activateIntegrationRequest: (
      state,
      action: PayloadAction<{ platform: string; restaurantId: string; fromName?: string }>
    ) => {
      state.activating = action.payload.platform;
      state.error = null;
    },
    activateIntegrationSuccess: (state) => {
      state.activating = null;
      state.error = null;
    },
    activateIntegrationFailure: (state, action: PayloadAction<string>) => {
      state.activating = null;
      state.error = action.payload;
    },
    
    // Deactivate integration
    deactivateIntegrationRequest: (
      state,
      action: PayloadAction<{ restaurantId: string; integrationId: string; platform: string }>
    ) => {
      state.deactivating = action.payload.platform;
      state.error = null;
    },
    deactivateIntegrationSuccess: (state, action: PayloadAction<{ integrationId: string }>) => {
      state.deactivating = null;
      state.error = null;
      // Remove the deleted integration from data array
      state.data = state.data.filter(int => int.id !== action.payload.integrationId);
    },
    deactivateIntegrationFailure: (state, action: PayloadAction<string>) => {
      state.deactivating = null;
      state.error = action.payload;
    },
    
    // Reset error
    clearIntegrationError: (state) => {
      state.error = null;
    },
    
    // Facebook auth callback
    facebookAuthCallbackRequest: (
      state,
      action: PayloadAction<{ code: string; state: string; error: string | null; platform?: string }>
    ) => {
      state.activating = action.payload.platform || 'facebook';
      state.error = null;
    },
    facebookAuthCallbackSuccess: (state, action: PayloadAction<Integration>) => {
      state.activating = null;
      state.error = null;
      // Add the new integration to the data array
      state.data = [...state.data, action.payload];
    },
    facebookAuthCallbackFailure: (state, action: PayloadAction<string>) => {
      state.activating = null;
      state.error = action.payload;
    },

    // Update TikTok default post settings
    updateTikTokSettingsRequest: (
      state,
      action: PayloadAction<{
        integrationId: string;
        restaurantId: string;
        settings: {
          disable_duet: boolean;
          disable_stitch: boolean;
          disable_comment: boolean;
          brand_content_toggle: boolean;
          brand_organic_toggle: boolean;
          is_aigc?: boolean;
          privacyLevel: string;
        };
      }>
    ) => {
      state.updatingSettings = true;
      state.updateSettingsError = null;
    },
    updateTikTokSettingsSuccess: (state, action: PayloadAction<Integration>) => {
      state.updatingSettings = false;
      state.updateSettingsError = null;
      const index = state.data.findIndex((int) => int.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = action.payload;
      }
    },
    updateTikTokSettingsFailure: (state, action: PayloadAction<string>) => {
      state.updatingSettings = false;
      state.updateSettingsError = action.payload;
    },
  },
});

export const {
  fetchIntegrationsRequest,
  fetchIntegrationsSuccess,
  fetchIntegrationsFailure,
  activateIntegrationRequest,
  activateIntegrationSuccess,
  activateIntegrationFailure,
  deactivateIntegrationRequest,
  deactivateIntegrationSuccess,
  deactivateIntegrationFailure,
  clearIntegrationError,
  facebookAuthCallbackRequest,
  facebookAuthCallbackSuccess,
  facebookAuthCallbackFailure,
  updateTikTokSettingsRequest,
  updateTikTokSettingsSuccess,
  updateTikTokSettingsFailure,
} = integrationsSlice.actions;


export default integrationsSlice.reducer;
